import { BaseCounter } from './BaseCounter.js';
import {
  createSkippingPredictor,
  extractSkippingTemporalFeatures,
  SKIPPING_TEMPORAL_WINDOW
} from '../utils/xgboostSkippingPredictor.js';

/**
 * XGBoost Jump Counter
 *
 * Uses a trained XGBoost model with temporal features (±5 frames) to detect jumps.
 * Runs entirely in the browser - no backend calls needed.
 */
export class XGBoostJumpCounter extends BaseCounter {
  static get id() {
    return 'skipping';
  }

  static get displayName() {
    return 'Skipping';
  }

  static get description() {
    return 'Jump rope detection using XGBoost ML with pose temporal features';
  }

  constructor(options = {}) {
    super();
    this.repCount = 0;

    // Alias strokeCount to repCount for consistency with other counters
    Object.defineProperty(this, 'strokeCount', {
      get: () => this.repCount,
      set: (val) => { this.repCount = val; }
    });

    // Options
    this.modelUrl = options.modelUrl || '/skipping-model/xgboost-jump-model.json';
    this.threshold = options.threshold || 0.5;
    this.frameW = options.frameW || 640;
    this.frameH = options.frameH || 480;

    // Temporal window settings
    this.BUFFER_SIZE = SKIPPING_TEMPORAL_WINDOW * 2 + 1; // 9 frames (±4)

    // Frame buffer for temporal features (stores keypoints arrays)
    this.frameBuffer = [];
    this.frameIndex = 0;
    this.prevShiftedKeypoints = null;

    // Jump detection state - use FRAME-based debounce
    // MIN_FRAMES_BETWEEN_JUMPS = 4 (similar to strokes)
    this.MIN_FRAMES_BETWEEN_JUMPS = 4;
    this.lastJumpFrameIndex = -this.MIN_FRAMES_BETWEEN_JUMPS - 1;

    // XGBoost predictor
    this.predictor = createSkippingPredictor();
    this.isModelLoaded = false;
    this.isLoading = false;
    this.loadError = null;

    // Debug info
    this.lastProbability = 0;
    this.predictionsThisSession = 0;

    // Model version info
    this.modelInfo = {
      skippingModel: null,
      skippingMeta: null
    };
  }

  /**
   * Initialize the model (call before using)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async initialize() {
    if (this.isModelLoaded) {
      return { success: true };
    }
    if (this.isLoading) {
      return { success: false, error: 'Model is still loading' };
    }

    this.isLoading = true;
    this.loadError = null;

    try {
      // Resolve model URL from manifest (supports content-addressed filenames)
      let actualModelUrl = this.modelUrl;
      const modelDir = this.modelUrl.replace(/\/[^/]+$/, '');

      try {
        const manifestUrl = `${modelDir}/manifest.xgboost.counter.json?_=${Date.now()}`;
        const manifestResp = await fetch(manifestUrl, { cache: 'no-store' });

        if (manifestResp.ok) {
          const manifest = await manifestResp.json();
          if (manifest.xgboostModel) {
            actualModelUrl = `${modelDir}/${manifest.xgboostModel}`;
            // Store model info for debugging/display
            this.modelInfo = {
              skippingModel: manifest.xgboostModel,
              skippingMeta: manifest.xgboostMeta || null
            };
          }
        }
      } catch (e) {
        // No manifest found, use default URL
      }

      const success = await this.predictor.load(actualModelUrl);
      this.isModelLoaded = success;

      if (success) {
        this.isLoading = false;
        return { success: true };
      } else {
        this.loadError = this.predictor.loadError || `Failed to load XGBoost model from ${actualModelUrl}`;
        console.error('XGBoostJumpCounter:', this.loadError);
        this.isLoading = false;
        return { success: false, error: this.loadError };
      }
    } catch (error) {
      this.loadError = `Failed to load XGBoost model: ${error.message}`;
      console.error('XGBoostJumpCounter:', this.loadError);
      this.isLoading = false;
      return { success: false, error: this.loadError };
    }
  }

  /**
   * Update the counter with pose keypoints
   * @param {Array} keypoints - MoveNet pose keypoints array
   * @param {number} timestamp - Unused, kept for API compatibility
   * @param {Object} videoDimensions - Optional {width, height} of source video
   * @returns {number} Current jump count
   */
  update(keypoints, timestamp = null, videoDimensions = null) {
    if (!this.isModelLoaded) {
      // Try to initialize if not loaded
      if (!this.isLoading) {
        this.initialize();
      }
      return this.repCount;
    }

    // Update frame dimensions from video if provided
    if (videoDimensions && videoDimensions.width && videoDimensions.height) {
      if (this.frameW !== videoDimensions.width || this.frameH !== videoDimensions.height) {
        this.frameW = videoDimensions.width;
        this.frameH = videoDimensions.height;
      }
    }

    // Store keypoints array directly
    this.frameBuffer.push(keypoints || []);
    if (this.frameBuffer.length > this.BUFFER_SIZE) {
      this.prevShiftedKeypoints = this.frameBuffer.shift();
    }
    this.frameIndex++;

    // Need full buffer for prediction
    if (this.frameBuffer.length < this.BUFFER_SIZE) {
      return this.repCount;
    }

    // Calculate the actual frame index being predicted (center of buffer)
    const predictedFrameIndex = this.frameIndex - 1 - SKIPPING_TEMPORAL_WINDOW;

    // Extract features for the center frame
    const centerIdx = SKIPPING_TEMPORAL_WINDOW;

    const features = extractSkippingTemporalFeatures(
      this.frameBuffer,
      centerIdx,
      this.frameW,
      this.frameH,
      this.prevShiftedKeypoints
    );

    // Run XGBoost prediction
    const probability = this.predictor.predictProba(features);
    this.lastProbability = probability;
    this.predictionsThisSession++;

    // Check if jump detected
    const isJump = probability >= this.threshold;

    // Use FRAME-based debounce
    const framesSinceLastJump = predictedFrameIndex - this.lastJumpFrameIndex;

    if (isJump && framesSinceLastJump > this.MIN_FRAMES_BETWEEN_JUMPS) {
      this.repCount++;
      this.lastJumpFrameIndex = predictedFrameIndex;
    }

    return this.repCount;
  }

  /**
   * Reset the counter
   */
  reset() {
    this.repCount = 0;
    this.frameBuffer = [];
    this.frameIndex = 0;
    this.prevShiftedKeypoints = null;
    this.lastJumpFrameIndex = -this.MIN_FRAMES_BETWEEN_JUMPS - 1;
    this.lastProbability = 0;
    this.predictionsThisSession = 0;
  }

  /**
   * Get current state for debugging
   */
  getState() {
    const predictedFrameIndex = this.frameIndex >= this.BUFFER_SIZE
      ? this.frameIndex - 1 - SKIPPING_TEMPORAL_WINDOW
      : -1;
    return {
      repCount: this.repCount,
      isModelLoaded: this.isModelLoaded,
      isLoading: this.isLoading,
      loadError: this.loadError,
      bufferSize: this.frameBuffer.length,
      frameIndex: this.frameIndex,
      predictedFrameIndex,
      lastProbability: this.lastProbability,
      predictionsThisSession: this.predictionsThisSession,
      threshold: this.threshold,
      framesSinceLastJump: predictedFrameIndex - this.lastJumpFrameIndex,
      modelInfo: this.modelInfo
    };
  }
}
